import { useCallback, useEffect, useRef, useState } from 'react';
import { loadSet, saveSet } from '../api/setsApi';
import { normalizeModules } from '../utils/setModel';

const AUTOSAVE_DELAY = 1200;

export default function useSetEditor(setId) {
  const [modules, setModules] = useState([]);
  const [isLoading, setIsLoading] = useState(Boolean(setId));
  const [canEdit, setCanEdit] = useState(false);
  const [ownerId, setOwnerId] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [error, setError] = useState('');
  const revisionRef = useRef(0);
  const savedRevisionRef = useRef(0);
  const savingRef = useRef(false);
  const modulesRef = useRef(modules);
  modulesRef.current = modules;

  useEffect(() => {
    let active = true;
    if (!setId) {
      setIsLoading(false);
      setError('This page needs a setId query parameter.');
      return undefined;
    }
    setIsLoading(true);
    loadSet(setId).then((payload) => {
      if (!active) return;
      setModules(normalizeModules(payload.modules));
      setCanEdit(Boolean(payload.canEdit));
      setOwnerId(payload.setOwnerId ?? null);
      setError('');
      revisionRef.current = 0;
      savedRevisionRef.current = 0;
    }).catch((loadError) => {
      if (active) setError(loadError.message);
    }).finally(() => {
      if (active) setIsLoading(false);
    });
    return () => { active = false; };
  }, [setId]);

  const updateModules = useCallback((next) => {
    setModules((current) => typeof next === 'function' ? next(current) : next);
    revisionRef.current += 1;
    setSaveStatus('pending');
  }, []);

  const persist = useCallback(async () => {
    if (!setId || !canEdit || savingRef.current || savedRevisionRef.current === revisionRef.current) return;
    savingRef.current = true;
    const savingRevision = revisionRef.current;
    setSaveStatus('saving');
    try {
      await saveSet(setId, modulesRef.current);
      savedRevisionRef.current = savingRevision;
      setSaveStatus(revisionRef.current === savingRevision ? 'saved' : 'pending');
      setError('');
    } catch (saveError) {
      setSaveStatus('error');
      setError(saveError.message);
    } finally {
      savingRef.current = false;
      if (savedRevisionRef.current !== revisionRef.current) {
        window.setTimeout(persist, 0);
      }
    }
  }, [canEdit, setId]);

  useEffect(() => {
    if (saveStatus !== 'pending' || !canEdit) return undefined;
    const timer = window.setTimeout(persist, AUTOSAVE_DELAY);
    return () => window.clearTimeout(timer);
  }, [canEdit, modules, persist, saveStatus]);

  useEffect(() => {
    if (saveStatus !== 'saved') return undefined;
    const timer = window.setTimeout(() => setSaveStatus('idle'), 1800);
    return () => window.clearTimeout(timer);
  }, [saveStatus]);

  return { modules, updateModules, isLoading, canEdit, ownerId, saveStatus, error, persist };
}
